
import Page from '@root/common/base/Page';
import Meta from '@root/common/utils/Meta';
import AdsLayout from '@root/common/components/layout/AdsLayout.vue';
import PodcastEpisodeService from '@root/modules/podcast/services/podcastEpisode.service';
import PodcastPaywall from '@root/modules/podcast/components/PodcastPaywall.vue';
import getMediaData from '@root/modules/podcast/utils/getMediaData';
import { limit } from '@root/modules/podcast/config/podcast.config';
import getFrontPageAdsSettings from '@root/modules/frontpage/utils/getFrontPageAdsSettings';
import isMobileView from '@root/common/utils/isMobileView';
import { Podcast } from '@root/modules/podcast/types/podcast';
import { getListenedDuration, getListenedIds } from '@root/modules/podcast/utils/customerData';
import Episode from '@root/modules/podcast/components/fragments/Episode.vue';
import PianoCustomerStateHandler from '@piano/components/PianoCustomerStateHandler.vue';
import resort from '@root/modules/podcast/utils/resort';

interface Data {
  episodes: Podcast[];
  isMobileView: boolean;
  loaded: boolean;
  limit: number;
  offset: number;
  shoudLoadMoreEpisodes: boolean;
  loadingEpisodes: boolean;
}

interface Computed {
  customerToken: string | null;
  showPaywall: boolean;
  showPlayer: boolean;
}

interface Methods {
  getListeningHistory: () => void;
  isBook: (episode: Podcast) => boolean;
  loadMoreEpisodes: () => void;
}

export default Page.extend<Data, Methods, Computed, unknown>({
  components: {
    AdsLayout,
    PodcastPaywall,
    Episode,
    PianoCustomerStateHandler,
  },
  data() {
    return {
      episodes: [],
      isMobileView: false,
      loaded: false,
      limit,
      offset: 0,
      shoudLoadMoreEpisodes: true,
      loadingEpisodes: false,
    };
  },
  computed: {
    customerToken() {
      return this.$store.state.piano.token;
    },
    showPaywall() {
      return this.$store.state.podcast.showPaywall;
    },
    showPlayer() {
      return Boolean(this.$player?.state.currentlyPlaying.audioId);
    },
  },
  watch: {
    customerToken(value) {
      if (value) {
        this.getListeningHistory();
      }
    },
  },
  methods: {
    async getListeningHistory() {
      if (!this.customerToken) {
        return;
      }

      const listenedIds = await getListenedIds(undefined, this.customerToken, this.limit, this.offset);
      if (listenedIds?.items?.length) {
        const articleIds = listenedIds.items.map((listenedId) => listenedId.value);
        const listenedDurations = await getListenedDuration(articleIds, this.customerToken);

        const podcastEpisodeService = new PodcastEpisodeService();
        const articles = await podcastEpisodeService.fetch({ id: articleIds.map((id) => Number(id)) });
        if (articles?.length) {
          const mediaData = await getMediaData(articles, null, false);
          const episodes = [];
          for (const article of resort(articles, articleIds)) {
            const duration = mediaData?.find((mediaDataItem) => mediaDataItem.id === article.id)?.assetDuration || null;
            const listenedDuration = Number(listenedDurations?.find((item) => item.key === `podcast-listened-duration--${article.id}`)?.value);

            episodes.push({ ...article, duration, listenedDuration });
          }

          this.episodes = [...this.episodes, ...episodes];
        }
      }
    },
    isBook(episode) {
      return episode.primaryCategory.parentCategory?.parentCategory?.id === 96603927;
    },
    async loadMoreEpisodes() {
      if (this.loadingEpisodes || !this.shoudLoadMoreEpisodes || !this.customerToken) {
        return;
      }

      const d = document.documentElement;
      const w = document.querySelector('.podcast-history') as HTMLElement;
      if (w) {
        const offsetThreshold = d.clientHeight + 500;
        const userHasScrolled = window.scrollY > 200;
        const offset = window.scrollY + offsetThreshold;

        if (userHasScrolled && offset >= w.offsetHeight) {
          this.loadingEpisodes = true;
          const listenedIds = await getListenedIds(undefined, this.customerToken, this.limit, this.offset + this.limit);
          if (listenedIds?.items?.length) {
            const articleIds = listenedIds.items.map((listenedId) => listenedId.value);
            const listenedDurations = await getListenedDuration(articleIds, this.customerToken);

            const podcastEpisodeService = new PodcastEpisodeService();
            const id = listenedIds?.items?.map((listenedId) => Number(listenedId.value));
            const articles = await podcastEpisodeService.fetch({ id });
            if (articles?.length) {
              const mediaData = await getMediaData(articles, null, false);
              const episodes = [];
              for (const article of resort(articles, articleIds)) {
                const duration = mediaData?.find((mediaDataItem) => mediaDataItem.id === article.id)?.assetDuration || null;
                const listenedDuration = Number(listenedDurations?.find((item) => item.key === `podcast-listened-duration--${article.id}`)?.value);

                episodes.push({ ...article, duration, listenedDuration });
              }

              this.episodes = [...this.episodes, ...episodes];
              this.offset = this.offset + this.limit;
            } else {
              this.shoudLoadMoreEpisodes = false;
            }
          } else {
            this.shoudLoadMoreEpisodes = false;
          }

          this.loadingEpisodes = false;
        }
      }
    },
  },
  beforeRouteLeave(to, from, next) {
    this.resetPageAdsSettings();

    next();
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.loadMoreEpisodes);
  },
  async mounted() {
    this.restoreScroll();
    this.isMobileView = isMobileView();
    this.setPageAdsSettings(getFrontPageAdsSettings({ $channelConfig: this.$channelConfig }));

    if (this.customerToken) {
      await this.getListeningHistory();
    }

    window.addEventListener('scroll', this.loadMoreEpisodes);
  },
  head() {
    const meta = new Meta(this.$channelConfig('meta').base, this.$route.path);

    meta.title = meta.ogTitle = this.$t('podcast.meta.history.title') as string;
    meta.description = meta.ogDescription = this.$t('podcast.meta.history.description') as string;

    return {
      title: meta.title,
      meta: meta.data,
      link: meta.link,
    };
  },
});
